import React, { useEffect, useState, useRef, memo } from "react";
import { SVGRenderer } from "echarts/renderers";
import * as echarts from "echarts/lib/echarts";
import { BarChart, LineChart, ScatterChart, PieChart } from "echarts/charts";
import {
  LegendComponent,
  AxisPointerComponent,
  GridComponent,
  DataZoomComponent,
  TooltipComponent
} from "echarts/components";
import PropTypes from "kf-proptypes/widgets";

import { useUserStore } from "base/stores";
import { THEME } from "base/constants";
import { getThemingProperty } from "base/chartthemeutil";

import {
  colorTemplates,
  CHART_DEFAULT_DESIGN_MIN_WIDTH,
  CHART_COLOR_VARIABLES
} from "./constant.js";
import {
  configureEvents,
  destroyEvents,
  getChartConfigurations
} from "./utils.js";
import { eventConfiguration } from "./event.configuration.js";

import styles from "./styles.css";

echarts.use([
  BarChart,
  LineChart,
  ScatterChart,
  PieChart,
  SVGRenderer,
  LegendComponent,
  AxisPointerComponent,
  GridComponent,
  DataZoomComponent,
  TooltipComponent
]);

export const DynamicChart = memo((chartData) => {
  const {
    data,
    rows,
    type,
    scaletype,
    events,
    animation = true,
    isPrintMode,
    onChartClicked = () => {},
    enablePattern,
    parentElementId = ""
  } = chartData;
  const uniqueId = useRef(Math.random());
  const theme =
    useUserStore((state) => state.theme) || localStorage.getItem("Theme");
  const [delayPatternEnable, setDelayPatternEnable] = useState(false);
  const [isSmallChart, setIsSmallChart] = useState(true);

  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  useEffect(
    function delayFunction() {
      const timeoutId = setTimeout(() => {
        if (enablePattern) {
          setDelayPatternEnable(true);
        } else {
          setDelayPatternEnable(false);
        }
      }, 1000);

      return () => clearTimeout(timeoutId);
    },
    [enablePattern]
  );

  useEffect(
    function ChartRenderer() {
      const chartTemplates = getThemingProperty(
        chartRef.current,
        CHART_COLOR_VARIABLES
      );
      let lightMode = true;

      if (!isPrintMode) {
        if (theme === THEME.DARK || theme === THEME.DIM) {
          lightMode = false;
        } else if (theme === "system_default") {
          const prefersDarkScheme = window.matchMedia(
            "(prefers-color-scheme: dark)"
          );
          lightMode = !prefersDarkScheme.matches;
        } else if (theme === THEME.LIGHT) {
          lightMode = true;
        }
      }

      const element = document.getElementById(`main${uniqueId.current}`);
      var chart = echarts.init(element, null, {
        renderer: "svg"
      });

      const { chartColors } = colorTemplates(lightMode);
      const chartOptions = getChartConfigurations({
        chartData,
        chartColors,
        chartTemplates,
        isSmallChart,
        enablePattern: delayPatternEnable,
        chartInstance: chart
      });

      let notMerge = true;
      let lazyUpdate = true;
      let replaceMerge = "xAxis";
      chartOptions.animation = animation;
      chartOptions &&
        chart.setOption(chartOptions, notMerge, replaceMerge, lazyUpdate);
      chartInstanceRef.current = chart;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, type, theme, scaletype, delayPatternEnable, isSmallChart]
  );

  useEffect(
    function initEvents() {
      if (chartInstanceRef.current && events) {
        configureEvents(events, chartInstanceRef.current);
      }

      return () => {
        if (chartInstanceRef.current && events)
          destroyEvents(events, chartInstanceRef.current);
      };
    },
    [events]
  );

  // pattern effect specific on click, mouseover, mouseclick
  useEffect(
    function initPatternEvents() {
      let chartEvents = {};
      if (delayPatternEnable) {
        const { click, mouseover, mouseout } = eventConfiguration(
          chartInstanceRef.current,
          onChartClicked,
          rows
        );
        chartEvents = { click, mouseover, mouseout };
        configureEvents(
          { click, mouseover, mouseout },
          chartInstanceRef.current
        );
      }

      return () => {
        if (chartInstanceRef.current) {
          destroyEvents(
            {
              click: chartEvents.click,
              mouseover: chartEvents.mouseover,
              mouseout: chartEvents.mouseout
            },
            chartInstanceRef.current
          );
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [delayPatternEnable, events, rows]
  );

  useEffect(function getSize() {
    const chart = document.getElementById(`main${uniqueId.current}`);
    const ParentElement = document.getElementById(parentElementId);

    var myChartSize = echarts.init(chart);

    const resizeObserver = new ResizeObserver(function sizeRezie() {
      myChartSize.resize();
      const isSmallChart =
        chartRef.current?.clientWidth < CHART_DEFAULT_DESIGN_MIN_WIDTH;
      setIsSmallChart(isSmallChart);
    });

    const resizeTimer = setTimeout(() => {
      ParentElement && resizeObserver.observe(ParentElement);
    }, 1000);
    return () => {
      resizeObserver.unobserve(chart);
      clearTimeout(resizeTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.chartOuterContainer}>
      <div
        style={{
          height: "100%",
          width: "100%",
          minHeight: "25.5rem"
        }}
        ref={chartRef}
        id={`main${uniqueId.current}`}
      />
    </div>
  );
});

DynamicChart.propTypes = {
  data: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.string),
    xAxisLabel: PropTypes.arrayOf(PropTypes.string),
    vals: PropTypes.arrayOf(
      PropTypes.shape({
        Id: PropTypes.string.isRequired,
        Name: PropTypes.string.isRequired,
        Type: PropTypes.string.isRequired,
        AggregationFunction: PropTypes.string.isRequired,
        IsGeneratedField: PropTypes.bool.isRequired
      })
    ),
    cols: PropTypes.arrayOf(
      PropTypes.shape({
        Id: PropTypes.string.isRequired,
        Name: PropTypes.string.isRequired,
        Type: PropTypes.string.isRequired
      })
    ),
    data: PropTypes.arrayOf(
      PropTypes.objectOf(
        PropTypes.oneOfType([PropTypes.number, PropTypes.string])
      )
    ),
    series: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number))
  }),
  type: PropTypes.string
};
